import { Coverage, CoverageEventType } from "@graphql/api";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EventTypeFormData } from "@routing/Residents/AddCoverageEvent/EventType/validation";

import { EventEditableFields } from "./types";
import { RootState } from "@redux/store";

export type EventType = Exclude<
  CoverageEventType,
  CoverageEventType.AddedActive | CoverageEventType.AddedInactive
>;

export interface EventSlice {
  eventType?: EventType;
  preEventCoverage?: Coverage;
}

const initialState: EventSlice = {};

const eventSlice = createSlice({
  name: "eventState",
  initialState,
  reducers: {
    /**
     * Sets the event type in the event slice.
     * @param state
     * @param action
     */
    setEventType: (state, action: PayloadAction<EventTypeFormData>) => {
      state.eventType = action.payload.eventType as EventType;
    },
    /**
     * Sets the pre-event coverage in the event slice.
     * @param state
     * @param action
     */
    setPreEventCoverage: (state, action: PayloadAction<Coverage>) => {
      state.preEventCoverage = action.payload;
    },
    /**
     * Resets the event slice to its initial state.
     */
    resetEventSlice: () => initialState,
  },
});

export const selectEventType = (state: RootState): EventType | undefined => {
  return state.event.eventType;
};

export const selectPreEventCoverage = (
  state: RootState
): Coverage | undefined => {
  return state.event.preEventCoverage;
};

export const selectEventEditableFields = (
  state: RootState
): EventEditableFields => {
  switch (state.event.eventType) {
    case CoverageEventType.Renewal:
      return {
        allOtherFieldsEditable: true,
        policyDescriptionEditable: true,
        policyStartDateEditable: false,
        policyEndDateEditable: true,
      };
    case CoverageEventType.Endorsement:
    case CoverageEventType.Reinstatement:
      return {
        allOtherFieldsEditable: true,
        policyDescriptionEditable: true,
        policyStartDateEditable: false,
        policyEndDateEditable: false,
      };
    case CoverageEventType.Cancellation:
      return {
        allOtherFieldsEditable: false,
        policyDescriptionEditable: true,
        policyStartDateEditable: false,
        policyEndDateEditable: true,
      };
    case CoverageEventType.NonRenewal:
      return {
        allOtherFieldsEditable: false,
        policyDescriptionEditable: true,
        policyStartDateEditable: false,
        policyEndDateEditable: false,
      };
    default:
      return undefined;
  }
};

export const { resetEventSlice, setEventType, setPreEventCoverage } =
  eventSlice.actions;
export default eventSlice;
